import { useMemo } from 'react';
import { QueryKey, useQuery, UseQueryOptions } from 'react-query';
import { useAuthQuery } from '../../../../utils/queryUtils';
import { Title } from '../../titles/types/title';
import {
  productsQuery,
  purchasedTitlesQuery,
  purchasesQuery,
  rentalsQuery,
  rentedTitlesQuery,
  subscribedProductsQuery,
  subscribedTitlesQuery,
} from './queries';
import { Product, Purchase, Rental, SubscribedProduct } from './types';

export function useProducts<T = Product[]>(
  platformTitleId: string,
  options?: UseQueryOptions<Product[], unknown, T, QueryKey>,
) {
  return useQuery(productsQuery(platformTitleId, options));
}

export function usePurchases<T = Purchase[]>(options?: UseQueryOptions<Purchase[], unknown, T, QueryKey>) {
  return useAuthQuery(purchasesQuery(options), ['user', 'home']);
}

export function useRentals<T = Rental[]>(options?: UseQueryOptions<Rental[], unknown, T, QueryKey>) {
  return useAuthQuery(rentalsQuery(options), ['user', 'home']);
}

/**
 * A list of purchased and rented products combined
 */
export function useSubscribedProducts<T = SubscribedProduct[]>(
  options?: UseQueryOptions<SubscribedProduct[], unknown, T, QueryKey>,
) {
  return useAuthQuery(subscribedProductsQuery(options), ['user', 'home']);
}

export function usePurchasedTitles<T = Title[]>(options?: UseQueryOptions<Title[], unknown, T, QueryKey>) {
  return useAuthQuery(purchasedTitlesQuery(options), ['user', 'home']);
}

export function useRentedTitles<T = Title[]>(options?: UseQueryOptions<Title[], unknown, T, QueryKey>) {
  return useAuthQuery(rentedTitlesQuery(options), ['user', 'home']);
}

export function useSubscribedTitles<T = Title[]>(options?: UseQueryOptions<Title[], unknown, T, QueryKey>) {
  return useAuthQuery(subscribedTitlesQuery(options), ['user', 'home']);
}

export function useSubscribedProductsForTitle(masterId: string | undefined) {
  const { data: titles, error: titleError, isLoading: isLoadingTitle } = useSubscribedTitles();
  const { data: products, error: productsError, isLoading: isLoadingProducts } = useSubscribedProducts();

  const isLoading = isLoadingTitle || isLoadingProducts;
  const error = titleError || productsError;

  const data = useMemo(() => {
    if (titles === undefined) {
      return undefined;
    }

    const title = titles.find((item) => {
      // Series can't be purchased directly
      if (item.type === 'episode') {
        return item.series.masterId === masterId || item.masterId === masterId;
      }

      return item.masterId === masterId;
    });

    if (title === undefined || products === undefined) {
      return undefined;
    }

    return products.filter((product) => product.masterId === title.masterId);
  }, [titles, products, masterId]);

  if (isLoading) {
    return { data: undefined, error: null, isLoading };
  }

  return { data, error, isLoading };
}
